<template>
  <div class="">
    <!-- Displayed Columns Section -->
    <div class="flex flex-col gap-3">
      <h3 class="text-lg font-semibold text-primary m-0">
        Displayed columns
      </h3>
      <div class="flex flex-col mb-3">
        <div
          v-for="(column, index) in displayedColumns"
          :key="column.key"
          class="py-3 px-2 bg-surface-light border-b border-weak cursor-pointer last:border-b-0"
          :class="{
            'cursor-grab': column.title != 'Name',
            'hover:bg-surfaces-neutral': column.title != 'Name',
            'pointer-events-none': column.title == 'Name',
            'opacity-50 bg-surfaces-neutral': column.dragging,
          }"
          :draggable="column.title != 'Name'"
          @dragstart="dragStart(index, $event)"
          @dragover.prevent
          @dragenter.prevent
          @drop="drop(index, $event)"
        >
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <AM3Icon
                v-if="!column.freeze"
                type="drag"
                size="sm"
                class="text-secondary mr-2 cursor-grab"
              />
              <span>{{ column.title }}</span>
            </div>
            <AM3Toggle
              v-model="column.visible"
              :model-value="true"
              :disabled="column.freeze"
              :data-test-id="`columns-edit-toggle-${column.key}`"
            />
          </div>
        </div>
        <div
          v-if="displayedColumns.length === 0"
          class="text-secondary italic p-2 text-center"
        >
          No columns selected
        </div>
      </div>
    </div>

    <!-- Standard Fields Section -->
    <div class="flex flex-col gap-3 mb-3">
      <h3 class="text-lg font-semibold text-primary m-0">
        Standard fields
      </h3>
      <div class="flex flex-col ">
        <div
          v-for="column in standardFields"
          :key="column.key"
          class="py-3 px-2 bg-surface-light border-b border-weak cursor-pointer last:border-b-0"
        >
          <div class="flex justify-between items-center">
            <span>{{ column.title }}</span>
            <AM3Toggle
              v-model="column.visible"
              :model-value="false"
              :data-test-id="`columns-edit-toggle-${column.key}`"
            />
          </div>
        </div>
        <div
          v-if="standardFields.length === 0"
          class="text-secondary italic p-2 text-center"
        >
          All standard fields are displayed
        </div>
      </div>
    </div>

    <!-- Custom Fields Section -->
    <div class="flex flex-col gap-3 mb-3">
      <h3 class="text-lg font-semibold text-primary m-0">
        Custom fields
      </h3>
      <div class="flex flex-col ">
        <div
          v-for="column in customFields"
          :key="column.key"
          class="py-3 px-2  bg-surface-light border-b border-weak cursor-pointer last:border-b-0"
        >
          <div class="flex justify-between items-center">
            <span>{{ column.title }}</span>
            <AM3Toggle
              v-model="column.visible"
              :model-value="false"
              :data-test-id="`columns-edit-toggle-${column.key}`"
            />
          </div>
        </div>
        <div
          v-if="customFields.length === 0"
          class="text-secondary text-sm italic p-2 text-center"
        >
          {{ hasAnyCustomFields ? 'All custom fields are displayed' : 'You have not created any custom fields' }}
        </div>
      </div>
    </div>
  </div>

  <!-- Actions -->
  <Teleport to="#drawer-footer">
    <div class="flex gap-3">
      <AM3Button
        text="Cancel"
        type="tertiary"
        data-test-id="reset-to-default-button"
        @click="resetToDefault"
      />
      <AM3Button
        text="Update columns"
        type="primary"
        class="flex-1"
        data-test-id="apply-changes-button"
        @click="applyChanges"
      />
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import type { PropType } from 'vue'
import { useStore } from 'vuex'

import type { DataListColumn } from './AM3DataList.vue'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import AM3Toggle from '@/components/am3-generic/inputs/selection/AM3Toggle.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'

export default defineComponent({
  name: 'AM3ColumnEdit',

  components: {
    AM3Button,
    AM3Toggle,
    AM3Icon,
  },

  props: {
    columns: {
      type: Array as PropType<DataListColumn[]>,
      required: true,
    },
    onUpdate: {
      type: Function as PropType<(columns: DataListColumn[]) => void>,
      required: true,
    },
    isDrawerOpen: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const store = useStore()
    // Create a deep copy of the columns to work with locally
    const selectedColumns = ref<DataListColumn[]>(JSON.parse(JSON.stringify(props.columns)))
    let draggedItemIndex = -1

    // Track the maximum order value to append new columns at the end, this works like an index.
    const getMaxOrder = () => {
      const visibleColumns = selectedColumns.value.filter(col => col.visible)
      return visibleColumns.length > 0
        ? Math.max(...visibleColumns.map(col => col.order !== undefined ? col.order : 0))
        : -1
    }

    // Create a reusable sorting function
    const sortColumnsByOrder = (columns: DataListColumn[]) => {
      return [...columns].sort((a, b) => {
        // If both columns have an order property, sort by order
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order
        }
        // If only a has an order, it comes first
        if (a.order !== undefined) {
          return -1
        }
        // If only b has an order, it comes first
        if (b.order !== undefined) {
          return 1
        }
        // If neither has an order, maintain original order
        return 0
      })
    }

    // Compute displayed columns (those that are visible) from the local copy
    const displayedColumns = computed(() => {
      const visibleColumns = selectedColumns.value.filter(col => col.visible)
      return sortColumnsByOrder(visibleColumns)
    })

    // Compute standard fields (those that are not visible and not custom) from the local copy
    const standardFields = computed(() => {
      return selectedColumns.value.filter(col =>
        !col.freeze && !col.custom && !col.visible,
      )
    })

    // Compute custom fields (those that are not visible and are custom) from the local copy
    const customFields = computed(() => {
      return selectedColumns.value.filter(col =>
        !col.freeze && col.custom && !col.visible,
      )
    })

    // Check if any custom fields exist at all
    const hasAnyCustomFields = computed(() => {
      return selectedColumns.value.some(col => col.custom)
    })

    // Drag and drop handlers
    const dragStart = (index: number, event: DragEvent) => {
      draggedItemIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        // Set some data to make drag work in Firefox
        event.dataTransfer.setData('text/plain', index.toString())
      }
    }

    const drop = (index: number, event: DragEvent) => {
      event.preventDefault()
      if (draggedItemIndex === -1 || draggedItemIndex === index) return

      // Get the visible columns
      const visibleCols = displayedColumns.value

      // Get the dragged column
      const draggedColumn = visibleCols[draggedItemIndex]

      // Create a new array with the reordered columns
      const updatedColumns = [...selectedColumns.value]

      // Create a new array of visible columns with the dragged item moved to the new position
      const newVisibleColumns = [...visibleCols]
      newVisibleColumns.splice(draggedItemIndex, 1)
      newVisibleColumns.splice(index, 0, draggedColumn)

      // Update the order property for all visible columns
      newVisibleColumns.forEach((col, idx) => {
        const originalCol = updatedColumns.find(c => c.key === col.key)
        if (originalCol) {
          originalCol.order = idx
        }
      })

      // Update the local columns
      selectedColumns.value = updatedColumns

      // Reset the dragged item index
      draggedItemIndex = -1
    }

    // Reset to default (all columns visible)
    const resetToDefault = () => {
      store.commit('globalDrawerModule/SET_IS_DRAWER_OPEN', false)
    }

    // Apply changes and close drawer - this is where changes are sent to the parent
    const applyChanges = () => {
      // Sort the columns by their order property before updating
      const sortedColumns = JSON.parse(JSON.stringify(sortColumnsByOrder(selectedColumns.value)))
      props.onUpdate(sortedColumns)
      store.commit('globalDrawerModule/SET_IS_DRAWER_OPEN', false)
    }

    // Watch for changes in column visibility
    watch(selectedColumns, (newColumns) => {
      newColumns.forEach((column) => {
        // When a column becomes visible and doesn't have an order, add it at the end
        if (column.visible && column.order === undefined) {
          column.order = getMaxOrder() + 1
        }
      })
    }, { deep: true })

    return {
      selectedColumns,
      displayedColumns,
      standardFields,
      customFields,
      hasAnyCustomFields,
      dragStart,
      drop,
      resetToDefault,
      applyChanges,
    }
  },
})
</script>
